import {ref, computed, Ref} from '@vue/composition-api';
import {
  Activity,
  CandidateInformationItem,
  CandidatePoiItem,
  CroppedGoalItem,
  getBeginningOfTheDay,
  getSolutionDates,
  IDateDescription,
  UserActivityCategoryEnum,
} from '@mobi-components/global-solution';
import {
  getAmexSolution,
  getManualActivities,
  getRecommendationDetails,
  getSolutionRecommendations,
} from '@/store/state/helpers';
import {RecommendationsContextWithOffers, SuggestionPois} from '@/views/amex/app-types';
import {RecommendationContext, TripIntent} from '@mobi-components/bluesky-utils';
import {formatBudget} from '@mobi-components/amex-bluesky-components';
import {PlaceCardItem} from '@common/place-card-logic/place-card-logic';
import {formatDate, getListOfSolutionDays} from '@/store/state/utils';
import {getavailableDatesForPoi} from '../../explore-page/exp-list/utils';
import {fetchAdditionalPois} from '@/store/api/fetch-additional-pois';
import {AdditionalPoisModule} from '@/store/modules';

const NUMBER_ON_THE_ROW = 6;

export const getCandidatePoiItem = (x: CandidateInformationItem): CandidatePoiItem => {
  const selection = x.selection;
  const candidate0 = x.candidates?.length ? x.candidates[0] : selection;
  return candidate0 as CandidatePoiItem;
};

const emptyTrip = {
  id: '',
  tags: [],
  images: {
    medium: '',
  },
  accommodations: [],
  flights: [],
  pois: 0,
  prices: [],
};

export function useGroup(
  category: Ref<UserActivityCategoryEnum>,
  isLoading: Ref<boolean>,
  activeSolutionFilters: Ref<Set<string>>,
  selectedExperiences: Ref<Set<string>>,
  pendingActivity: Ref<RecommendationContext | null>,
  isDeprecated: boolean,
) {
  const showAllGropus = ref<Record<string, number>>({});
  const additionaGroupPois = ref<Record<string, RecommendationsContextWithOffers[]>>({});
  const disabledShowMore = ref<Record<string, boolean>>({});

  const solution = computed(() => {
    return getAmexSolution();
  });

  const solutionActivitiesByPoiId = computed(() => {
    return (solution.value?.activities || []).reduce((res, activity) => {
      res.set(activity?.source?.candidate_id || '', activity);
      return res;
    }, new Map<string, Activity>());
  });

  const solutionDates = computed<IDateDescription[]>(() => {
    if (!solution.value) {
      return [];
    }
    const days = getListOfSolutionDays(solution.value);
    return getSolutionDates(days).map((x, index) => ({
      ...x,
      label: `Day ${index + 1}`,
      description: formatDate(x.date),
    }));
  });

  const getDayForActivity = (activity?: Activity) => {
    if (!activity) {
      return '';
    }
    const start = getBeginningOfTheDay(new Date(activity.scheduled_start_time * 1000));
    const date = (solutionDates.value || []).find((x) => x.date.valueOf() === start.valueOf());
    return date?.label || '';
  };

  const allRecommendationsWithDays = computed<Map<string, string[]>>(() => {
    const availablePois = getSolutionRecommendations();
    const dates = solutionDates.value.map((x) => x.date);

    return availablePois.reduce((res, x) => {
      const datesForPoi = new Set(getavailableDatesForPoi(x, dates).map((date) => date.valueOf()));
      const labels = solutionDates.value.filter((x) => datesForPoi.has(x.date.valueOf())).map((x) => x.label);
      res.set(x.source.content?.id || '', labels);
      return res;
    }, new Map());
  });

  const getRecommendedDayLabel = (activityId: string) => {
    const options = allRecommendationsWithDays.value.get(activityId) || [];
    if (options.length) {
      return options[0];
    }
    return '';
  };

  const getGroupName = (x: SuggestionPois) => {
    return x.theme ? `<b style="text-transform: capitalize">${x.theme}</>` : 'Best for your Trip';
  };

  const onShowAllInTheGroup = async (name: string) => {
    const numberOfItemsToShow = 30;
    showAllGropus.value = {
      ...showAllGropus.value,
      [name]: (showAllGropus.value[name] || NUMBER_ON_THE_ROW) + numberOfItemsToShow,
    };
    const group = allGroups.value.find((x) => name === getGroupName(x));
    if (!group) {
      return;
    }
    const groupName = getGroupName(group);
    const startIndex = group.pois.length + (additionaGroupPois.value[groupName] || []).length;
    const poiIdsToRequest = (group.all_poi_ids || []).slice(startIndex, startIndex + numberOfItemsToShow);
    if (poiIdsToRequest.length === 0) {
      disabledShowMore.value = {
        ...disabledShowMore.value,
        [groupName]: false,
      };
      return;
    }

    // TODO: use LOAD ADDITIONAL_POIS here, to auti-save pois.
    const pois = await fetchAdditionalPois(poiIdsToRequest);
    if (pois && pois.length) {
      AdditionalPoisModule.addPois(pois);
    }

    additionaGroupPois.value[groupName] = additionaGroupPois.value[groupName] || [];
    additionaGroupPois.value = {
      ...additionaGroupPois.value,
      [groupName]: additionaGroupPois.value[groupName].concat(pois),
    };
  };

  const allGroups = computed<SuggestionPois[]>(() => {
    const allPoiGroups = getRecommendationDetails() || {};
    return allPoiGroups[category.value] || [];
  });

  const getFormattedName = (fullName: string, type: string, day: string) => {
    const cutName = (fullName || '')
      .replace('LUNCH:', '')
      .replace('BREAKFAST:', '')
      .replace('DINNER:', '')
      .trim();
    const typeName = (fullName || '')
      .replace(cutName, '')
      .replace(':', '')
      .trim()
      .toLowerCase();
    const lowerType = type.toLocaleLowerCase();
    const formattedType = (lowerType && lowerType.charAt(0).toUpperCase() + lowerType.slice(1)) || '';
    const formattedTypeName = (typeName && typeName.charAt(0).toUpperCase() + typeName.slice(1)) || '';
    const readableType = formattedTypeName || formattedType;

    return `${readableType} ${day}`.trim();
  };

  const getCards = (pois: RecommendationContext[] = []): PlaceCardItem[] => {
    const manualActivities = new Set(getManualActivities().map((x) => x.id));
    return (
      pois
        // ?.slice(0, 12)
        .map((x) => {
          const allTags = new Set(x.tags || []);

          const matchedTags = new Set(
            Array.from(activeSolutionFilters.value).filter((y) => allTags.has(y)),
            // .slice(0, 3),
          );
          const disabledTags = [...allTags].filter((y) => !matchedTags.has(y));

          // const usefulDisabled = disabledTags.slice(0, 3 - matchedTags.size);

          const tags = [...matchedTags]
            .map((y) => ({name: y, isActive: true}))
            // .concat([...usefulDisabled].map((y) => ({name: y, isActive: false})));
            .concat([...disabledTags].map((y) => ({name: y, isActive: false})));

          const isDine = (x.categories || []).includes(UserActivityCategoryEnum.DINING);
          const priceBound = x.price_level?.lowerbound ? new Array(x.price_level.lowerbound).fill('$').join('') : '';
          const usdPrice = x.prices?.length ? formatBudget(x.prices[0].amount, x.prices[0].currency, 'Points') : '';
          const totalPrice = (isDine && priceBound) || usdPrice;
          const category = (x.categories || [])[0] || '';
          const day = getDayForActivity(solutionActivitiesByPoiId.value.get(x.id));

          return {
            isSelected: selectedExperiences.value.has(x.id) || selectedExperiences.value.has(x.source_id),
            isSaved: manualActivities.has(x.id) || manualActivities.has(x.source_id),

            isPending: pendingActivity.value?.id === x.id,
            dayDetails: getFormattedName(solutionActivitiesByPoiId.value.get(x.id)?.name || '', category, day),
            day,
            get isStay() {
              return (x.categories || []).includes(UserActivityCategoryEnum.ACCOMMODATION);
            },
            id: x.id,
            rating: x.rating,
            name: x.name,
            description: (x.explanations || []).join(', '),
            img: x.images?.medium,
            price: totalPrice,
            tags,
            numberOfTags: matchedTags.size,
            isRecommendation: allRecommendationsWithDays.value.has(x.id),
            location: x.location,
            //@ts-ignore
            parent: x.parent_id,
            //@ts-ignore
            children: x.children_ids,
          };
        })
        // .sort((a, b) => b.numberOfTags - a.numberOfTags)
    );
  };

  const availableGroups = computed(() => {
    const groups = allGroups.value;

    if (isDeprecated) {
      const recommendations = (solution.value?.activities || [])
        .filter((x) => [UserActivityCategoryEnum.EXPERIENCE, UserActivityCategoryEnum.DINING].includes(x.category))
        .filter((x) => !x.spec?.curated)
        .filter((x) => x.source?.content)
        .map((x) => x.source.content)
        .filter(Boolean) as CandidatePoiItem[];

      const manualActivities = getManualActivities();

      const droppedPois = solution.value?.dropped_activities
        .filter((x) => x.candidate_information?.candidates?.length)
        .map((x: CroppedGoalItem) => getCandidatePoiItem(x.candidate_information))
        .filter((x) => x.images)  as CandidatePoiItem[];

      const pois = [...(recommendations || []), ...(manualActivities || []), ...(droppedPois || [])].map(
        (poiItem: CandidatePoiItem): RecommendationContext => {
          const category = poiItem.categories?.length ? poiItem.categories[0] : UserActivityCategoryEnum.NONE;

          return {
            ...poiItem,
            intent: TripIntent.MAYBE,
            matchedFilters: [],
            curated: false,
            recommendations: [],
            context: category,
            contexts: [category],
            // @ts-ignore
            explanations: poiItem.explanation?.descriptions || [],
            scheduled_end_time: poiItem.start_timestamp || 0,
            scheduled_start_time: poiItem.end_timestamp || 0,
          };
        },
      ) as RecommendationContext[];
      const savedCards = getCards(pois);

      return [
        {
          name: '',
          cards: savedCards,
          allCards: savedCards,
          isAllShown: true,
          isShowAllAvailable: false,
          showAllCaption: '',
        },
      ];
    }

    if (groups.length === 0 && isLoading.value) {
      const emptyCards = [
        {...emptyTrip, id: '1'},
        {...emptyTrip, id: '2'},
        {...emptyTrip, id: '3'},
        {...emptyTrip, id: '4'},
        {...emptyTrip, id: '5'},
        {...emptyTrip, id: '6'},
      ];

      return [
        {
          name: 'Best for your Trip',
          cards: emptyCards,
          allCards: emptyCards,
          isAllShown: true,
          isShowAllAvailable: false,
          showAllCaption: '',
        },
      ];
    }

    return groups.map((x) => {
      const groupName = getGroupName(x);
      const length = showAllGropus.value[groupName] || NUMBER_ON_THE_ROW;
      
      const cards = getCards(x.pois.concat(additionaGroupPois.value[groupName] || []));

      return {
        name: groupName,
        cards: cards.slice(0, length),
        allCards: cards,
        isAllShown: false,
        isShowAllAvailable: cards.length > NUMBER_ON_THE_ROW && disabledShowMore.value[groupName] !== false,
        showAllCaption: 'Show More',
      };
    });
  });

  return {
    showAllGropus,
    availableGroups,
    onShowAllInTheGroup,
    getRecommendedDayLabel,
  };
}
